import { useMemo, useState } from "react";
import type { MeResponse } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { SESSION_PARTIAL_TEXT } from "../auth/auth-errors.ts";
import {
  filterZoneGroups,
  guessTimeZone,
  onlyZone,
  timeZoneGroups,
  zoneLabel,
  zonesDiffer,
} from "../../lib/time-zone.ts";
import { Dialog } from "./Dialog.tsx";

/**
 * Окно часового пояса. Две точки входа, и обе просил преподаватель 20.08.2026.
 *
 * 1. **Первое подтверждение.** Раньше это была полоса сверху, и отзыв был
 *    дословный: «уведомление про часовой пояс высветилось как шторка сверху, и
 *    я прям на автомате щёлкнул ок». Просьба тоже дословная: «наоборот надо
 *    навязчиво». Окно поперёк экрана нажимается осознанно, полоса над рабочей
 *    областью нажимается мимоходом, и в этом вся разница
 * 2. **Смена пояса когда угодно**, из строки «время в зоне …» на расписании.
 *    До этой правки сменить пояс было НЕЛЬЗЯ НИЧЕМ, кроме случая, когда часы
 *    браузера начинали спорить с записанным. Человек, подтвердивший зону под
 *    VPN и не выключивший VPN, оставался с неверным временем каждого занятия
 *    навсегда: расхождения нет, значит и вопроса нет, а другой двери не было
 *
 * ⚠️ **Спрашивать в комнате урока по-прежнему запрещено**, и держится это тем
 * же, чем держалось у полосы: обе точки входа стоят в каркасе кабинета, а
 * комната урока лежит ВНЕ него (`Shell`). Человек, спешащий на занятие,
 * которого уже ждут, нажмёт что угодно, лишь бы убрать окно, и мы получим
 * подтверждённой зону аэропортового вайфая. Модальное окно делает эту ошибку
 * дороже полосы, поэтому правило важнее, чем было.
 *
 * ⚠️ Догадка браузера ПРЕДЛАГАЕТСЯ, а не применяется, и это не изменилось:
 * `resolvedOptions()` ошибается на VPN, на чужом ноутбуке и в командировке.
 * Поэтому здесь две кнопки, а не ноль. Канонизирует имя сервер.
 */
export function TimeZoneDialog(props: {
  me: MeResponse;
  onDone: (me: MeResponse) => void;
  /**
   * Закрыть, ничего не записав.
   *
   * ⚠️ У первого подтверждения это НЕ «больше не спрашивать»: ничего не
   * записано, значит при следующей загрузке кабинета окно встанет снова, а до
   * тех пор остаётся полоса (`TimeZoneGate`). Лестница здесь намеренная:
   * сначала навязчиво, потом тихо, но не молча.
   */
  onClose: () => void;
}) {
  const confirmed = props.me.timeZone ?? null;
  const guess = useMemo(guessTimeZone, []);
  /*
    В список обязаны попасть ОБА значения: догадка браузера и уже записанное.
    Записанного в `Intl.supportedValuesOf` может не быть вовсе (`UTC` там нет,
    а старые представители переименованных зон вроде `Asia/Calcutta` исчезают
    со сменой ICU), и тогда `<select value={confirmed}>` показал бы пустоту
    вместо выбранного. Разбор у самой функции.
  */
  const groups = useMemo(() => timeZoneGroups(guess, confirmed ?? ""), [guess, confirmed]);
  /**
   * Что стоит в выборе при открытии.
   *
   * У первого подтверждения это догадка браузера, у смены пояса записанное
   * значение. Разница смысловая: в первом случае мы предлагаем то, что видим, а
   * во втором показываем то, что записано, и подменять его догадкой нельзя,
   * иначе «Изменить» под VPN молча предложило бы записать зону VPN.
   */
  const [choice, setChoice] = useState(confirmed ?? guess);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Что набрано в поиске. Живёт до закрытия окна и никуда не сохраняется. */
  const [query, setQuery] = useState("");

  const found = useMemo(() => filterZoneGroups(groups, query), [groups, query]);

  /**
   * Что показать списком: найденное, а сверху выбранное, если поиск его спрятал.
   *
   * ⚠️ **Выбранная зона обязана быть в списке ВСЕГДА, и это не удобство.**
   * React у управляемого `<select>` со значением, которого нет среди
   * `<option>`, ставит selectedIndex не в −1, а на ПЕРВУЮ строку списка
   * (`updateOptions` в ReactDOMSelect). Получалось так: выбран Владивосток,
   * набрали «казах», и список подсвечивал Алматы, хотя «Сохранить» записал бы
   * Владивосток. Два разных ответа на один вопрос, причём неверный крупнее.
   *
   * Пустое значение это не лечит по той же причине: пустой строки среди
   * вариантов тоже нет, и React снова встаёт на первую. Проверено на стенде,
   * а не выведено из документации: первая попытка чинила это как раз пустым
   * значением и не работала.
   *
   * Поэтому выбранное просто не пропадает: своей группой сверху, когда фильтр
   * его не пропустил. Подсветка списка после этого равна выбору по построению,
   * и отдельная строка «будет записан» больше не нужна.
   */
  const shown = useMemo(() => {
    if (found.some((group) => group.zones.some((z) => z.zone === choice))) return found;
    return [{ title: "Выбрано", zones: [{ zone: choice, label: zoneLabel(choice) }] }, ...found];
  }, [found, choice]);

  /**
   * Набор текста сужает список, а сузившись до одной зоны, ВЫБИРАЕТ её.
   *
   * ⚠️ Выбор стоит в обработчике, а не в эффекте по `shown`. Эффект переписывал
   * бы выбор и при стирании текста, когда список снова расширяется до одной
   * зоны на обратном пути, то есть отбирал бы у человека уже сделанный выбор. А
   * так выбор меняется ровно на нажатие клавиши и ровно тогда, когда сомнений в
   * намерении нет: подходит одна зона на весь реестр.
   */
  const search = (raw: string) => {
    setQuery(raw);
    const single = onlyZone(filterZoneGroups(groups, raw));
    if (single !== null) setChoice(single);
  };

  /*
    ⚠️ По поведению, а не по строке, и той же функцией, что строка «время в
    зоне …» на расписании. Сравнение строкой объявляло бы расхождением
    переименование зоны в новом ICU (`Europe/Kiev` против `Europe/Kyiv`), и
    окно противоречило бы строке, из которой его только что открыли: там
    расхождения нет, здесь есть.
  */
  const drifted = zonesDiffer(confirmed, guess);

  async function save(zone: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      props.onDone(await api.setTimeZone(zone));
      props.onClose();
    } catch (e) {
      // Вкладка с устаревшим признаком полноты (9.6 docs/account-adopt-design.md):
      // повтор тут бесполезен, сказать надо, где пояс поменять можно
      setError(
        e instanceof api.ApiError && e.code === "session_partial"
          ? SESSION_PARTIAL_TEXT
          : "не удалось сохранить, попробуйте ещё раз",
      );
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={confirmed === null ? "Ваш часовой пояс" : "Часовой пояс"}
      note="От него зависит время каждого занятия."
      onClose={props.onClose}
      foot={
        <>
          {/*
            Отказ тише подтверждения. У первого подтверждения он не «больше не
            спрашивать», а «не сейчас»: записывать нечего, и окно вернётся при
            следующей загрузке кабинета.
          */}
          <button type="button" className="btn btn--quiet" disabled={busy} onClick={props.onClose}>
            {confirmed === null ? "Позже" : "Отмена"}
          </button>
          {/*
            Акцент здесь законен, в отличие от полосы: у окна свой экран, и
            главное действие на нём ровно одно. Подпись говорит про запись, а не
            про согласие: «Да, верно» на автомате нажимается лучше, чем
            «Сохранить», и ровно это с полосой и произошло.
          */}
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void save(choice)}
          >
            Сохранить
          </button>
        </>
      }
    >
      <div className="form">
        <p className="muted">
          {confirmed === null ? (
            <>
              Похоже, у вас <b title={guess}>{zoneLabel(guess)}</b>. Проверьте: если это не так,
              выберите свой в списке.
            </>
          ) : (
            <>
              Ваш пояс: <b title={confirmed}>{zoneLabel(confirmed)}</b>.
              {/*
              Догадка называется только тогда, когда она СПОРИТ с записанным.
              Совпадающая строка «а часы показывают то же самое» это шум, из
              которого человеку нечего сделать.

              Слова «вы переехали?» здесь нет и не будет: отличить переезд от
              VPN браузер не умеет, и вопрос, на который у нас нет данных,
              оставляет человека доказывать нам, где он находится. Поэтому это
              факт, а не догадка о его причине.
            */}
              {drifted && (
                <>
                  {" "}
                  Часы устройства показывают <b title={guess}>{zoneLabel(guess)}</b>.
                </>
              )}
            </>
          )}
        </p>

        <label className="field">
          <span className="lbl">Поиск</span>
          {/*
            ⚠️ Именно `type="search"`, а не `text`: браузер даёт крестик очистки
            и не предлагает автозаполнение адресами, которое иначе накрывает
            список собой на первом же символе.

            Фокус ставится только у смены пояса. У первого подтверждения его
            нет намеренно: там самое частое действие это «Сохранить» с догадкой,
            а поднятая на телефоне клавиатура закрыла бы половину окна ради
            поиска, который человеку не нужен.
          */}
          <input
            className="fld"
            type="search"
            value={query}
            disabled={busy}
            autoFocus={confirmed !== null}
            placeholder="город, страна или GMT+3"
            onChange={(e) => search(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="lbl">Часовой пояс</span>
          {/*
            Значением `<option>` остаётся имя IANA, подпись только читается:
            локализация экранная, на проводе и в базе по-прежнему `Europe/Moscow`.
          */}
          <select
            className="tzpick fld"
            /*
              Список, а не выпадашка: со включённым поиском выпадашка означала
              бы «набери, потом открой и посмотри, что получилось», то есть
              результат поиска был бы не виден в момент набора. Восемь строк это
              компромисс между обзором и высотой окна на телефоне.
            */
            size={8}
            value={choice}
            disabled={busy}
            onChange={(e) => setChoice(e.target.value)}
          >
            {shown.map((group) =>
              group.title === null ? (
                group.zones.map((z) => (
                  <option key={z.zone} value={z.zone}>
                    {z.label}
                  </option>
                ))
              ) : (
                <optgroup key={group.title} label={group.title}>
                  {group.zones.map((z) => (
                    <option key={z.zone} value={z.zone}>
                      {z.label}
                    </option>
                  ))}
                </optgroup>
              ),
            )}
          </select>
        </label>

        {/*
          Пустой результат назван словами. Список из нуля строк это просто
          пустая рамка, из которой не следует, поиск ли ничего не нашёл или
          сломался экран.
        */}
        {found.length === 0 && (
          <p className="muted">Ничего не нашлось. Попробуйте название страны или смещение.</p>
        )}

        {error === null ? null : <p className="err">{error}</p>}

      </div>
    </Dialog>
  );
}
